<template>
  <v-app id="login">
    <div class="login-layout">
      <!-- Left branding panel -->
      <div class="login-brand hidden-sm-and-down">
        <div class="login-brand__content">
          <div class="login-brand__logo-wrap">
            <img src="/static/eduPass.jpeg" alt="edPass" class="login-brand__logo" />
          </div>
          <h1 class="login-brand__title">edPass</h1>
          <p class="login-brand__tagline">Smart school management, <br />simplified for everyone.</p>

          <div class="login-features">
            <div class="login-feature-item">
              <v-icon class="login-feature-item__icon">check_circle</v-icon>
              <span>Manage schools &amp; campuses</span>
            </div>
            <div class="login-feature-item">
              <v-icon class="login-feature-item__icon">check_circle</v-icon>
              <span>Real-time server monitoring</span>
            </div>
            <div class="login-feature-item">
              <v-icon class="login-feature-item__icon">check_circle</v-icon>
              <span>Module &amp; policy assignment</span>
            </div>
            <div class="login-feature-item">
              <v-icon class="login-feature-item__icon">check_circle</v-icon>
              <span>Attendance &amp; visitor tracking</span>
            </div>
          </div>
        </div>
        <div class="login-brand__footer">
          © {{ currentYear }} edPass — All rights reserved
        </div>
      </div>

      <!-- Right form panel -->
      <div class="login-form-panel">
        <div class="login-form-wrap">
          <!-- Mobile logo -->
          <div class="login-mobile-logo hidden-md-and-up">
            <img src="/static/eduPass.jpeg" alt="edPass" height="56" />
          </div>

          <div class="login-form-header">
            <h2 class="login-form-header__title">Welcome back</h2>
            <p class="login-form-header__sub">Sign in to your admin portal</p>
          </div>

          <v-form class="login-form" @keyup.native.enter="loginSubmitForm">
            <form-text-field
              v-model="login.username"
              label="Username"
              append-icon="person"
              name="login"
              placeholder="Enter your username"
              type="text"
            ></form-text-field>

            <form-text-field
              v-model="login.password"
              label="Password"
              append-icon="lock"
              name="password"
              placeholder="Enter your password"
              id="password"
              type="password"
            ></form-text-field>

            <v-btn
              block
              large
              color="primary"
              class="login-btn"
              @click="loginSubmitForm"
              :loading="loading"
              depressed
            >
              Sign In
            </v-btn>
          </v-form>

          <p class="login-footer-note">
            edPass Admin Portal &nbsp;·&nbsp; Secure access only
          </p>
        </div>
      </div>
    </div>
  </v-app>
</template>

<script>
import FormTextField from "@/components/form/FormTextField";
import mixinAuth from "@/mixins/APIAuth";
export default {
  components: {
    FormTextField
  },
  mixins: [mixinAuth],
  data: () => ({
    loading: false,
    login: {
      username: "",
      password: ""
    }
  }),
  computed: {
    currentYear() {
      return new Date().getFullYear();
    }
  },
  methods: {
    loginSubmitForm: async function() {
      this.loading = true;
      try {
        let response = await this.authLogin(this.login);
        if (response.status === 200) {
          localStorage.setItem("token", response.data.token);
          localStorage.setItem("type", response.data.role);
          localStorage.setItem("login_user", response.data._id);
          localStorage.setItem("active_user", JSON.stringify(response.data));
          setTimeout(() => {
            switch (response.data.role) {
              case 1:
                this.$router.push("/companies");
                this.$toast.success(
                  "Welcome!",
                  "Successfully logged in to eduPass",
                  { position: "topCenter" }
                );
                break;
              case 2:
                break;
              default:
            }
          }, 1000);
        }
      } catch (error) {
        this.$toast.error("Failed to login!", "Error", {
          position: "topCenter"
        });
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped lang="css">
/* Full-screen layout */
.login-layout {
  display: flex;
  min-height: 100vh;
  width: 100%;
}

/* Left branding panel */
.login-brand {
  flex: 0 0 42%;
  background: linear-gradient(145deg, #1565C0 0%, #0d47a1 60%, #0a2d6e 100%);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 48px 48px 32px;
  position: relative;
  overflow: hidden;
}

/* Subtle decorative circles */
.login-brand::before {
  content: '';
  position: absolute;
  top: -80px;
  right: -80px;
  width: 320px;
  height: 320px;
  border-radius: 50%;
  background: rgba(255,255,255,0.04);
}

.login-brand::after {
  content: '';
  position: absolute;
  bottom: -60px;
  left: -60px;
  width: 240px;
  height: 240px;
  border-radius: 50%;
  background: rgba(255,255,255,0.03);
}

.login-brand__content {
  position: relative;
  z-index: 1;
}

.login-brand__logo-wrap {
  width: 60px;
  height: 60px;
  border-radius: 16px;
  overflow: hidden;
  background: rgba(255,255,255,0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 28px;
}

.login-brand__logo {
  width: 60px;
  height: 60px;
  object-fit: cover;
}

.login-brand__title {
  font-family: 'Inter', 'Roboto', sans-serif;
  font-size: 2.6rem;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: -1px;
  margin-bottom: 16px;
  line-height: 1.1;
}

.login-brand__tagline {
  font-family: 'Inter', 'Roboto', sans-serif;
  font-size: 1.05rem;
  color: rgba(255,255,255,0.72);
  line-height: 1.6;
  margin-bottom: 40px;
  font-weight: 400;
}

.login-features {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.login-feature-item {
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: 'Inter', 'Roboto', sans-serif;
  font-size: 14px;
  color: rgba(255,255,255,0.8);
  font-weight: 400;
}

.login-feature-item__icon {
  font-size: 18px !important;
  color: rgba(100, 210, 255, 0.9) !important;
}

.login-brand__footer {
  position: relative;
  z-index: 1;
  font-size: 12px;
  color: rgba(255,255,255,0.35);
  font-family: 'Inter', 'Roboto', sans-serif;
}

/* Right form panel */
.login-form-panel {
  flex: 1;
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 24px;
}

.login-form-wrap {
  width: 100%;
  max-width: 400px;
}

.login-mobile-logo {
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
}

.login-form-header {
  margin-bottom: 32px;
}

.login-form-header__title {
  font-family: 'Inter', 'Roboto', sans-serif;
  font-size: 1.75rem;
  font-weight: 700;
  color: #1e293b;
  letter-spacing: -0.5px;
  margin-bottom: 6px;
}

.login-form-header__sub {
  font-family: 'Inter', 'Roboto', sans-serif;
  font-size: 14px;
  color: #64748b;
  font-weight: 400;
}

.login-form >>> .form-field {
  margin-bottom: 4px;
}

.login-btn {
  margin-top: 24px !important;
  height: 48px !important;
  border-radius: 10px !important;
  font-family: 'Inter', 'Roboto', sans-serif !important;
  font-size: 15px !important;
  font-weight: 600 !important;
  letter-spacing: 0.2px !important;
  text-transform: none !important;
  box-shadow: 0 4px 14px rgba(21, 101, 192, 0.35) !important;
}

.login-btn:hover {
  box-shadow: 0 6px 20px rgba(21, 101, 192, 0.45) !important;
}

.login-footer-note {
  text-align: center;
  margin-top: 24px;
  font-size: 12px;
  color: #94a3b8;
  font-family: 'Inter', 'Roboto', sans-serif;
}
</style>
